import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useFetchTableObjectInfoQuery, useFetchTableObjectStructureQuery } from "../../../api/rtk/base/slices/tableObjectApiSlice";
import { fetchObjectGeometry } from "../../../api/mapApi";
import { getItemWebStorage } from "../../../features/webStorageSaver";
import { useAppSelector } from "../../../stateManagement/hooks/redux";
import { setAddToFavouriteModalIsShown } from "../../../stateManagement/reducers/FavouritesSlice";
import { setTableObjectIsLoadingState } from "../../../stateManagement/reducers/TableObjectSlice";
import { AddFavouriteModal } from "../../../widgets/favourite/addFavouriteModal";
import { Loader } from "../../../widgets/loaders/Loader";
import { countExtentFromWKTFeatures } from "../../../widgets/map/mapFeatures/helpers";
import { getAllRoadObjectsLayer, getWKTFeatureVectorLayer } from "../../../widgets/map/mapFeatures/layers";
import MapWidget from "../../../widgets/map/mapWidget/MapWidget";
import { ObjectInfoCardSection } from "../../../widgets/objectInfoCardSection";
import { prepareObjectDataForFavourites } from "../../../features/dataFormatFunctions/dataFormatter";
import ContentHeader from "../../../widgets/contentHeader/ContentHeader";
import { setPrintButtonIsClicked } from "../../../stateManagement/reducers/ContentHeaderSlice";
import ToastMessageContainer from "../../../widgets/toastMessageContainer/ToastMessageContainer";
import { setToastMessageContainerClearButtonIsClicked, showToastMessage } from "../../../stateManagement/reducers";
import SmallLoader from "../../../widgets/loaders/SmallLoader";

function changeNoValueItemsStyle() {
    Array.prototype.forEach.call(document.getElementsByClassName("value"), function (el) {
        if (el.innerHTML === '') {
            el.textContent = "не указано"
        }
        if (el.innerHTML == "не указано" || el.innerHTML == null) {
            el.classList.add("basic-info-no-value")
        } else if (el.innerHTML !== "не указано" && el.innerHTML !== null) {
            el.classList.add("basic-info-value")
        }
    })
}

export function ObjectInfoCard() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { objectId, objectTypeName }: any = useParams()
    const printRef = useRef(null)
    const objectTypes = getItemWebStorage('objectTypes')
    const [mapState, setMapState] = useState<any>(null)
    const [printMapState, setPrintMapState] = useState<any>(null)
    const [mapEventHandlers, setMapEventHandlers] = useState<any>()
    const [name, setName] = useState<any>(null)
    const [showMap, setShowMap] = useState<boolean>(false)
    const [additionalLayers, setAdditionalLayers] = useState<any>([])
    const [additionalLayersForPrint, setAdditionalLayersForPrint] = useState<any>([])
    const [objectGeometryIsError, setObjectGeometryIsError] = useState<boolean>(false)
    const [objectGeometryIsFetching, setObjectGeometryIsFetching] = useState<boolean>(true)
    const [fitExtent, setFitExtent] = useState<any>();
    const [fitExtentPadding, setFitExtentPadding] = useState<any>([]);
    const [permanentLayersMap] = useState([getAllRoadObjectsLayer()])
    const [permanentLayersPrintMap] = useState([getAllRoadObjectsLayer()])

    const printButtonIsClicked = useAppSelector(state => state.contentHeader.printButtonIsClicked)

    const addressTypes = getItemWebStorage('addressTypes')
    let urlType = addressTypes.value.find((type: any) => type.name === objectTypeName) != null ? `address` : `objects`

    // запрос на получение структуры полей и разделов информации об объекте
    const {
        data: objectsCardsStructure,
        isFetching: objectsCardsStructureIsFetching,
        isError: objectsCardsStructureIsError,
        isSuccess: objectsCardsStructureSuccess
    } = useFetchTableObjectStructureQuery({})

    // запрос на получение информации об объекте
    const {
        data: objectInfo,
        error: objectInfoError,
        isFetching: objectInfoIsFetching
    } = useFetchTableObjectInfoQuery({ urlType, objectType: objectTypeName, objectId }, { skip: !objectsCardsStructureSuccess })

    const pathID = objectInfo && (objectInfo?.PathID ?? objectInfo?.PathId)
    const eventId = objectInfo && objectInfo?.EventID

    async function getObjectGeometry() {
        const { data: objectWKTGeometry, isError, isSuccess } = await fetchObjectGeometry(urlType, objectTypeName, objectId)
        setObjectGeometryIsError(isError)
        setObjectGeometryIsFetching(false)
        if (isSuccess && objectWKTGeometry) {
            const objectLayer = getWKTFeatureVectorLayer(objectWKTGeometry)
            const objectLayerForPrint = getWKTFeatureVectorLayer(objectWKTGeometry)
            setAdditionalLayers([objectLayer])
            setAdditionalLayersForPrint([objectLayerForPrint])
            const extent = countExtentFromWKTFeatures(objectWKTGeometry)
            setFitExtent(extent)
            setFitExtentPadding([50, 50, 50, 50])
            setShowMap(true)
        }
    }

    useEffect(() => {
        getObjectGeometry()
        return () => {
            dispatch(setAddToFavouriteModalIsShown(false))
            dispatch(setTableObjectIsLoadingState({
                objectInfo: null,
                tableObjectsStructure: null,
                loadingCoords: false,
                loadingStructure: false,
                loadingData: false
            }))
        }
    }, [])

    useEffect(() => {
        if (objectsCardsStructure && !objectsCardsStructure[objectTypeName]) {
            navigate('/app/error');
        }
    }, [objectsCardsStructure, objectTypeName])

    useEffect(() => {
        if (objectTypeName) {
            const captionSingular = objectTypes.find((objectType: any) => objectType?.name === objectTypeName)?.captionSingular
            setName(captionSingular)
        }
    }, [objectTypeName])

    useEffect(() => {
        if (objectInfoError) {
            let error: any = objectInfoError
            if (error?.data?.error?.code == 'EntityNotFound') {
                navigate('/app/error')
            }
        }
    }, [objectInfoError])

    /* Изменение внешнего вида полей с отсутствующим значением */
    useEffect(() => {
        changeNoValueItemsStyle()
        const newTableObjectLoadingState = {
            loadingStructure: objectsCardsStructureIsFetching,
            loadingData: objectInfoIsFetching
        }
        dispatch(setTableObjectIsLoadingState(newTableObjectLoadingState))
    }, [objectsCardsStructureIsFetching, objectInfoIsFetching])

    // функция печати страницы информации
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: 'IndorGeo ' + `- ${name}`,
        pageStyle: 'print'
    });

    useEffect(() => {
        // обработчик изменения флага-индикации нажатия кнопки печати
        if (printButtonIsClicked) {
            handlePrint()
            dispatch(setPrintButtonIsClicked(false))
        }
    }, [printButtonIsClicked])

    useEffect(() => {
        if (objectInfoIsFetching || objectsCardsStructureIsFetching || objectGeometryIsFetching) {
            dispatch(setToastMessageContainerClearButtonIsClicked(true))
        }
    }, [objectInfoIsFetching, objectsCardsStructureIsFetching, objectGeometryIsFetching])

    useEffect(() => {
        if (objectsCardsStructureIsError) {
            dispatch(showToastMessage({ messageText: "Не удалось загрузить страницу", messageType: "error" }))
        }
    }, [objectsCardsStructureIsError])

    useEffect(() => {
        if (objectGeometryIsError) {
            dispatch(showToastMessage({ messageText: "Не удалось загрузить положение объекта на карте", messageType: "error" }))
        }
    }, [objectGeometryIsError])

    useEffect(() => {
        if (objectInfoError) {
            dispatch(showToastMessage({ messageText: "Не удалось загрузить данные об объекте", messageType: "error" }))
        }
    }, [objectInfoError])

    useEffect(() => {
        function applyOpenFullSizeMap(map: any) {
            const clickFunc = (event: any) => navigate(`/app/map/${objectTypeName}/${objectId}`)
            map?.on("singleclick", clickFunc)
            return {
                off: () => {
                    map?.un("singleclick", clickFunc)
                }
            }
        }
        function applySynchronizePrintMap(map: any) {
            const synchFunc = (event: any) => {
                if (mapState && printMapState) {
                    printMapState.getView().fit(mapState.getView().calculateExtent(), {
                        size: printMapState.getSize()
                    });
                }
            }
            map?.on("moveend", synchFunc)
            return {
                off: () => {
                    map?.un("moveend", synchFunc)
                }
            }
        }
        setMapEventHandlers(
            [
                {
                    name: "openFullSizeMap",
                    func: applyOpenFullSizeMap
                },
                {
                    name: "synchronizePrintMap",
                    func: applySynchronizePrintMap
                }
            ]
        )
    }, [mapState, printMapState])

    return (
        <>
            <ContentHeader title={name} type="card" option="object" />
            {objectInfo && <AddFavouriteModal objectInfo={prepareObjectDataForFavourites(objectInfo)} />}
            <div className="table-object-info-page-div" ref={printRef}>
                <ToastMessageContainer />
                {(objectInfoIsFetching || objectsCardsStructureIsFetching)
                    ?
                    <Loader />
                    :
                    <div className="object-info-div" >
                        <h5 className="print-title">{name}</h5>
                        {/* Контейнер для отображения печатного варианта страницы */}
                        <div style={{ display: 'none' }}>
                            Дата номер
                        </div>
                        {/* Отображение разделов и полей информации для конкретного типа объекта */}
                        {objectInfo && objectsCardsStructure && objectsCardsStructure[objectTypeName].map((item: any) => (
                            <ObjectInfoCardSection
                                key={Object.keys(item)[0]}
                                sectionName={Object.keys(item)[0]}
                                sectionItems={item.data}
                                data={objectInfo}
                                eventId={eventId}
                                pathId={pathID}
                                showOnMapFlag={showMap}
                            />
                        ))
                        }
                        {objectGeometryIsFetching &&
                            <div className="map-widget-table-object-info loader">
                                <SmallLoader />
                            </div>
                        }
                        {(showMap && !objectGeometryIsError) &&
                            <div className="map-widget-table-object-info">
                                <MapWidget
                                    mapStateReturnFunc={(map: any) => setMapState(map)}
                                    additionalLayers={additionalLayers}
                                    permanentLayers={permanentLayersMap}
                                    propsFitExtent={fitExtent}
                                    propsFitExtentPadding={fitExtentPadding}
                                    mapEventHandlers={mapEventHandlers}
                                    mapInfoWidgetIsShown={false}
                                    mapType="card"
                                />
                            </div>
                        }
                        {(showMap && !objectGeometryIsError) &&
                            <div className="map-to-print-div">
                                <div className="page-break" />
                                <MapWidget
                                    target="table-object-info-map-print"
                                    mapStateReturnFunc={(map: any) => setPrintMapState(map)}
                                    additionalLayers={additionalLayersForPrint}
                                    permanentLayers={permanentLayersPrintMap}
                                    propsFitExtent={fitExtent}
                                    propsFitExtentPadding={fitExtentPadding}
                                    mapSizeClass="table-object-info-map-print"
                                    mapInfoWidgetIsShown={false}
                                    showMapLoader={false}
                                    mapType="card"
                                />
                            </div>
                        }
                    </div>
                }
            </div>
        </>
    )
}
